import { useEffect } from "react";
import { Metadata } from "../../types/Metadata";
import { RaportSection, ReportState } from "../../types/Rows";
import { useMetadata } from "../..";
import { cn } from "../../lib/utils";
import { useNavigate } from "react-router-dom";

const STATUS_META: Record<number, { label: string; pill: string; accent: string }> = {
  [ReportState.INPROGRESS]: { label: "W trakcie",       pill: "bg-[#FEF3E2] text-[#C07810]", accent: "#F4A832" },
  [ReportState.TO_VERIFY]:  { label: "Do weryfikacji",  pill: "bg-[#EDF3FC] text-[#3570C4]", accent: "#5B8FD8" },
  [ReportState.TO_CORRECT]: { label: "Do poprawy",      pill: "bg-[#FEE8E8] text-[#C03030]", accent: "#E05050" },
  [ReportState.ACCEPTED]:   { label: "Zaakceptowano",   pill: "bg-[#E6F7F1] text-[#1A8055]", accent: "#2BAE72" },
};

export const ReportHeader = ({
  name,
  address,
  resortName,
  created,
  state,
  sections,
  metadata,
  onOpenMetadataModal,
}: {
  name: string;
  address: string;
  resortName: string;
  created?: Date;
  state: ReportState;
  sections: RaportSection[];
  metadata: Metadata | undefined;
  onOpenMetadataModal?: () => void;
}) => {
  const { updateMetadata } = useMetadata();
  const navigate = useNavigate();

  useEffect(() => {
    if (metadata) {
      updateMetadata(() => metadata);
    }
  }, [metadata, updateMetadata]);

  const statusMeta = STATUS_META[state] ?? STATUS_META[0];
  const sortedSections = [...sections].sort((a, b) => a.weight < b.weight ? -1 : 1);

  return (
    <>
      {/* Report meta */}
      <div className="px-5 py-4 border-b border-[var(--sc-border)]">
        <div className="mb-1">
          <div className="text-[14px] font-semibold text-[var(--sc-text)] leading-snug">
            {name}
          </div>
        </div>
        <div className="text-[11px] text-[var(--sc-muted)] leading-[1.6]">
          {resortName}
          {address && <><br />{address}</>}
          {created && (
            <><br />{new Date(created).toLocaleDateString("pl-PL", { day: "numeric", month: "long", year: "numeric" })}</>
          )}
          {metadata?.visitDate && (
            <><br />{new Date(metadata.visitDate).toLocaleDateString("pl-PL", { day: "numeric", month: "long", year: "numeric" })}</>
          )}
        </div>
        <div className={cn("inline-flex items-center gap-1.5 mt-2.5 px-2.5 py-[5px] rounded-[20px] text-[11px] font-medium", statusMeta.pill)}>
          <span className="w-[5px] h-[5px] rounded-full flex-shrink-0" style={{ background: statusMeta.accent }} />
          {statusMeta.label}
        </div>
      </div>

      {/* Section navigation */}
      <div className="flex-1 overflow-y-auto py-3">
        <div className="text-[10px] font-medium uppercase tracking-[0.08em] text-[var(--sc-muted)] px-5 mb-2">
          Sekcje raportu
        </div>
        {sortedSections.map((section, i) => {
          const rootQs = (section.questionsWithAnswer ?? []).filter(q => !q.parentId);
          const total = rootQs.filter(q => !q.isBlank).length;
          const answered = rootQs.filter(q => !q.isBlank && q.answer && q.answer !== "").length;
          const isDone = total > 0 && answered === total;
          const fuCount = rootQs.filter(q => q.isPresentInFollowUpLetter).length;
          return (
            <div
              key={section.id}
              onClick={() => document.getElementById(`section-${section.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" })}
              className="flex items-center gap-2.5 px-5 py-[8px] text-left cursor-pointer hover:bg-[var(--sc-bg)] transition-colors"
            >
              <span className={cn("w-1.5 h-1.5 rounded-full flex-shrink-0", isDone ? "bg-[#2BAE72]" : "bg-[var(--sc-border)]")} />
              <span className="flex-1 truncate text-[12px] text-[var(--sc-muted)]">
                {i + 1}. {section.name}
              </span>
              <span className="text-[10px] text-[var(--sc-muted)] ml-auto flex-shrink-0">
                {isDone ? "✓" : `${answered}/${total}`}
                {fuCount > 0 && ` · ${fuCount}↑`}
              </span>
            </div>
          );
        })}
      </div>

      {/* Autosave indicator */}
      <div className="px-5 py-2.5 border-t border-[var(--sc-border)] flex items-center gap-2 text-[10px] text-[var(--sc-muted)] flex-shrink-0">
        <span className="w-[5px] h-[5px] rounded-full bg-[#2BAE72] flex-shrink-0" />
        Automatyczne zapisywanie
      </div>
    </>
  );
};
